import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { noop } from "lodash-es";
import { useTheme } from "styled-components";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { ButtonVariant } from "@viagogo/core/ui";
import { Content, useContent } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useFallbackImageSrc } from "src/shared/hooks/useFallbackImageSrc";
import { useInView } from "src/shared/hooks/useInView";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { storeAnalyticsEvent } from "src/shared/utils/analyticsevent/analyticsevent-batcher";
import { useIsStubhub } from "src/shared/utils/appContextHelper";
import { BigPerformerPaginationIndicator } from "src/website-apps/responsive/stubhub-home/components/tm-home-big-performer-redesign/BigPerformerPaginationIndicator";
import {
  SPACING_NARROW,
  SPACING_WIDE,
} from "src/website-apps/responsive/stubhub-home/components/tm-home-big-performer-redesign/PaginationDots";
import { SWIPE_RIGHT, useSwipeController } from "src/website-apps/responsive/stubhub-home/hooks/useSwipeController";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { HOME_CAROUSEL_IDS } from "src/website-apps/shared/components/responsive-carousel/constants";
import { SEARCH_DISCOVERY_ENTITY_TYPES } from "src/website-apps/shared/utils/searchPageEvents";
import { ClearHeroThemeProvider } from "./ClearHeroTheme";
import { StackedCard, StackedCards } from "./StackedCards";
import {
  BannerEyebrow,
  BannerSubTitle,
  BannerTitle,
  CTAContainer,
  EntryDetailsContainer,
  HeroContainer,
  HeroSection,
  HeroSectionDivider,
  HeroSectionImage,
  HeroSectionText,
  MobilePagination,
  StyledButton,
  StyledFavoritesButton,
  StyledImage,
} from "./styled-components";

const CTA_CONTENT_ID = "cid$#3107104#"; // See Tickets
const EYEBROW_CONTENT_ID = "cid$#3541353#"; // Featured

/**
 * Tracks analytics events for hero banner (clicks or impressions)
 * @param {string} eventName - Either "HomeCuratedContentClicks" or "HomeCuratedContentImpressions"
 * @param {object} appContext - Application context with user/session data
 * @param {object} env - Environment variables
 * @param {object} category - Hero banner category data
 */
const trackHeroBannerAnalytics = (eventName, appContext, env, category) => {
  const { anonymousId, pageVisitId, sessionId, userId, appVersion, cobrandId, clientApplicationId } = appContext;
  const collectionId = String(HOME_CAROUSEL_IDS.heroBanner);

  storeAnalyticsEvent(
    {
      applicationName: appContext.appName ?? "Unknown",
      applicationVersion: appVersion,
      eventName,
      eventBody: {
        collectionId,
        entityId: category.categoryId,
        entityTypeId: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
        entityPosition: -1, // Hero banner is in header, not part of body carousel ordering (which starts at 0)
        index: category.idx,
        categoryId: category.categoryId,
        eventId: undefined, // Hero banner shows categories, not events
        anonymousId,
        pageVisitId,
        sessionId,
        userId,
        appVersion,
        cobrandId,
        clientApplicationId,
        score: category.score,
        modelId: category.modelId,
      },
    },
    env,
  );
};

/**
 * Content of the Big Performer Banner
 */
export const ClearBannerContent = ({
  categories,
  showEyebrow = true,
  showFavorite = true,
  narrowTopMargin = false,
}) => {
  "use no memo";
  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const { env } = appContext;
  const fallbackImageSrc = useFallbackImageSrc();
  const isStubHub = useIsStubhub();
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const currentCategory = categories[currentCategoryIndex];
  const intervalRef = useRef();
  const viewedCategoriesRef = useRef(new Set()); // Track which categories have been viewed
  const eyebrowText = useContent(EYEBROW_CONTENT_ID) || "Featured";
  const theme = useTheme();

  // Track if hero banner is currently visible in viewport
  const { inView, ref: heroContainerRef } = useInView({
    threshold: 0.5, // Require 50% visibility
    fallbackInView: true,
  });

  // Engages page event after link to "see tickets" is clicked
  const handleLinkClick = useCallback(() => {
    storePageEvent(
      PE_TYPE.CLICK,
      PE_KEYS.HERO_BANNER_CTA_CLICK,
      `id=${currentCategory.categoryId}&idx=${currentCategory.idx}`,
    );
    trackHeroBannerAnalytics("HomeCuratedContentClicks", appContext, env, currentCategory);
  }, [appContext, env, currentCategory, storePageEvent]);

  const [enableAutoSlide, setEnableAutoSlide] = useState(true);
  const nextSlide = useCallback((index) => (index + 1 === categories.length ? 0 : index + 1), [categories]);
  const prevSlide = useCallback((index) => (index === 0 ? categories.length - 1 : index - 1), [categories]);

  /**
   * Track impression event when category is viewed (only once per category)
   * Only fires when hero banner is actually visible in viewport
   */
  useEffect(() => {
    if (!currentCategory || !inView) return;

    const { categoryId } = currentCategory;

    // Only track if this category hasn't been viewed yet
    if (!viewedCategoriesRef.current.has(categoryId)) {
      viewedCategoriesRef.current.add(categoryId);
      trackHeroBannerAnalytics("HomeCuratedContentImpressions", appContext, env, currentCategory);
    }
  }, [appContext, env, currentCategory, inView]);

  /**
   * Switch the current category to display the content for on interval
   */
  useEffect(() => {
    if (categories.length > 0 && enableAutoSlide) {
      const intervalId = setInterval(() => {
        setCurrentCategoryIndex(nextSlide);
      }, 3000);

      intervalRef.current = intervalId;
      return () => clearInterval(intervalId);
    }
    return noop;
  }, [categories, intervalRef, enableAutoSlide, nextSlide]);

  const onSlideChange = useCallback(
    (cat, index) => {
      clearInterval(intervalRef.current);
      setCurrentCategoryIndex(index);
      storePageEvent(PE_TYPE.CLICK, PE_KEYS.HERO_BANNER_PREVIEW_CLICK, `id=${cat.categoryId}&idx=${index}`);
    },
    [storePageEvent],
  );

  const heroContainer = useRef(null);
  const enableSwipe = isMobile;
  const swipeListeners = useMemo(
    () =>
      enableSwipe
        ? {
            onStart: () => setEnableAutoSlide(false),
            onEnd: () => setEnableAutoSlide(true),
            onSwipe: (p) =>
              p === SWIPE_RIGHT ? setCurrentCategoryIndex(prevSlide) : setCurrentCategoryIndex(nextSlide),
          }
        : null,
    [enableSwipe, nextSlide, prevSlide],
  );
  useSwipeController(heroContainer.current, swipeListeners);

  // Combine refs for both swipe control and in-view tracking
  const setRefs = useCallback(
    (node) => {
      heroContainer.current = node;
      heroContainerRef(node);
    },
    [heroContainerRef],
  );

  // Nothing to show if no categories exist
  if (!categories?.length) {
    return null;
  }

  return (
    <ClearHeroThemeProvider>
      <HeroContainer $narrowTopMargin={narrowTopMargin} ref={setRefs}>
        <StackedCards>
          {categories.map((cat, index) => (
            <StackedCard active={index === currentCategoryIndex} key={cat.categoryId}>
              <HeroSection onClick={handleLinkClick} url={cat.url}>
                <HeroSectionText isStubhub={isStubHub}>
                  <EntryDetailsContainer>
                    {showEyebrow ? <BannerEyebrow bold={!isMobile}>{eyebrowText}</BannerEyebrow> : null}
                    <BannerTitle $withSubtitle={!!cat.content}>{cat.categoryName}</BannerTitle>
                    {isMobile || !cat.content ? null : <BannerSubTitle>{cat.content}</BannerSubTitle>}
                    {isMobile ? null : (
                      <CTAContainer>
                        <StyledButton variant={ButtonVariant.outline}>
                          <Content id={CTA_CONTENT_ID} />
                        </StyledButton>
                      </CTAContainer>
                    )}
                  </EntryDetailsContainer>
                  {isMobile ? null : (
                    <BigPerformerPaginationIndicator
                      activeId={currentCategory?.categoryId}
                      items={categories}
                      onSlideChange={onSlideChange}
                    />
                  )}
                </HeroSectionText>

                <HeroSectionImage>
                  <StyledImage
                    alt=""
                    fallbackImageSrc={fallbackImageSrc}
                    imagePosition={cat?.imagePosition ?? "center"}
                    size={{ height: "100%", width: "100%" }}
                    src={cat.imageUrl}
                  />
                  {showFavorite ? (
                    <StyledFavoritesButton
                      authRedirectUrl={cat.url}
                      entityId={cat.categoryId}
                      entityType={ENTITY_TYPES.CATEGORY}
                      preventDefault
                      stopPropagation
                      aggregateFavorites={cat.aggregateFavorites}
                    />
                  ) : null}
                  <HeroSectionDivider isStubhub={isStubHub} isMobile={isMobile} />
                </HeroSectionImage>
              </HeroSection>
            </StackedCard>
          ))}
        </StackedCards>
      </HeroContainer>
      {isMobile ? (
        <MobilePagination>
          <BigPerformerPaginationIndicator
            hide={!isMobile}
            activeId={currentCategory?.categoryId}
            items={categories}
            onSlideChange={onSlideChange}
            color={theme.palette.textLinkNavPrimaryOnSurface}
            spacing={enableSwipe ? SPACING_NARROW : SPACING_WIDE}
          />
        </MobilePagination>
      ) : null}
    </ClearHeroThemeProvider>
  );
};
